import axios from 'axios';
import toast from 'react-hot-toast';
import { offlineManager } from './offlineManager';
import { logger } from './logger';
import { refreshAuthToken } from './authRefresh';
import { CSRF_HEADER_NAME, getCsrfToken } from './csrf';
import { isPublicPath } from '../config/publicRoutes';

// API Base URL - uses environment variable in production, localhost in development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://vineblock.onrender.com';

// Debug logging (development only)
logger.log('🔧 API Configuration:');
logger.log('  REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
logger.log('  API_BASE_URL:', API_BASE_URL);
logger.log('  NODE_ENV:', process.env.NODE_ENV);

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Required for HttpOnly auth cookie support
});

const READ_ONLY_STORAGE_KEY = 'vb_is_read_only';
const READ_ONLY_EXEMPT_PREFIXES = ['/api/billing'];
const READ_ONLY_EXEMPT_PATHS = new Set(['/api/auth/logout', '/api/auth/log-login', '/api/auth/password-changed']);
let lastReadOnlyToastAt = 0;
let lastReadOnlyToastPath = '';

function getPathname(url: string | undefined): string {
  if (!url) return '';
  try {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return new URL(url).pathname;
    }
    return new URL(url, API_BASE_URL).pathname;
  } catch {
    return url;
  }
}

function isMutationMethod(method: string | undefined): boolean {
  const normalized = (method || 'get').toLowerCase();
  return normalized === 'post' || normalized === 'put' || normalized === 'patch' || normalized === 'delete';
}

function isReadOnlyExempt(pathname: string): boolean {
  if (!pathname) return false;
  if (READ_ONLY_EXEMPT_PATHS.has(pathname)) return true;
  return READ_ONLY_EXEMPT_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function showReadOnlyToast(): void {
  const currentPath = window.location.pathname || '/';
  if (lastReadOnlyToastPath === currentPath) {
    return;
  }
  const now = Date.now();
  if (now - lastReadOnlyToastAt < 2500) {
    return;
  }
  lastReadOnlyToastAt = now;
  lastReadOnlyToastPath = currentPath;
  toast.error('Account is read-only. Reactivate subscription to make changes.');
}

function removeAuthorizationHeader(config: any): void {
  const headers = config?.headers as any;
  if (!headers) return;

  // Axios v1 may provide AxiosHeaders with delete()
  if (typeof headers.delete === 'function') {
    headers.delete('Authorization');
    headers.delete('authorization');
    return;
  }

  delete headers.Authorization;
  delete headers.authorization;
}

// Request interceptor to add auth token and handle offline mode
api.interceptors.request.use(
  async (config) => {
    // Cookie-session auth is our canonical browser auth transport.
    // Strip accidental bearer headers to avoid auth conflicts and logout loops.
    removeAuthorizationHeader(config);

    const pathname = getPathname(config.url);
    const isReadOnly = localStorage.getItem(READ_ONLY_STORAGE_KEY) === '1';
    if (isReadOnly && isMutationMethod(config.method) && !isReadOnlyExempt(pathname)) {
      const error: any = new Error('Account is in read-only mode due to subscription status.');
      error.code = 'READ_ONLY_MODE';
      error.config = config;
      error.response = {
        status: 403,
        data: {
          detail: 'Account is in read-only mode due to subscription status. Reactivate subscription to modify data.',
        },
      };
      showReadOnlyToast();
      return Promise.reject(error);
    }

    const method = (config.method || 'get').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        (config.headers as any)[CSRF_HEADER_NAME] = csrfToken;
      }
    }

    // Handle offline mode for GET requests
    if (config.method === 'get' && !offlineManager.isConnected()) {
      const cachedData = await offlineManager.getCachedData(config.url || '');
      if (cachedData) {
        logger.log('Returning cached data for offline request:', config.url);
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: cachedData,
            status: 200,
            statusText: 'OK',
            headers: {},
            config,
            request: {}
          })
        };
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and offline support
api.interceptors.response.use(
  async (response) => {
    // Cache successful GET responses for offline use
    if (response.config.method === 'get' && response.status === 200) {
      await offlineManager.cacheData(
        response.config.url || '', 
        response.data, 
        5 // 5 minutes TTL
      );
    }
    return response;
  },
  async (error) => {
    if (
      error?.response?.status === 403 &&
      typeof error?.response?.data?.detail === 'string' &&
      error.response.data.detail.toLowerCase().includes('read-only mode')
    ) {
      showReadOnlyToast();
    }

    // Handle offline mode for failed requests
    if (!offlineManager.isConnected() && error.code === 'NETWORK_ERROR') {
      logger.log('Network error, attempting offline handling:', error.config?.url);
      
      // For GET requests, try to return cached data
      if (error.config?.method === 'get') {
        const cachedData = await offlineManager.getCachedData(error.config.url || '');
        if (cachedData) {
          logger.log('Returning cached data for failed request:', error.config.url);
          return Promise.resolve({
            data: cachedData,
            status: 200,
            statusText: 'OK (Cached)',
            headers: {},
            config: error.config,
            request: {}
          });
        }
      }
      
      // For POST/PUT/DELETE requests, queue for offline processing
      if (['post', 'put', 'delete'].includes(error.config?.method || '')) {
        const requestId = await offlineManager.queueRequest(
          error.config.method.toUpperCase(),
          error.config.url || '',
          error.config.data ? JSON.parse(error.config.data) : {}
        );
        
        logger.log('Request queued offline:', requestId);
        return Promise.resolve({
          data: { 
            success: true, 
            offline: true, 
            message: 'Request queued for sync when online',
            id: requestId
          },
          status: 202,
          statusText: 'Accepted (Offline)',
          headers: {},
          config: error.config,
          request: {}
        });
      }
    }

    // On 401 / JWT expiry: try refresh once, then retry request; only redirect if refresh fails
    const isAuthError =
      error.response?.status === 401 ||
      (error.response?.data?.detail &&
        (String(error.response.data.detail).includes('expired') ||
          String(error.response.data.detail).includes('JWT')));
    if (isAuthError && error.config && !error.config.__retriedAuth) {
      const currentPath = window.location.pathname;
      if (isPublicPath(currentPath)) {
        // On landing, login, register, etc. 401 is expected; do not redirect.
      } else {
        const refreshed = await refreshAuthToken();
        if (refreshed) {
          (error.config as any).__retriedAuth = true;
          return api.request(error.config);
        }
        logger.log('Token refresh failed, redirecting to login');
        localStorage.removeItem('worker_login_time');
        if (!currentPath.includes('/login')) {
          const verticalSegment = currentPath.split('/').filter(Boolean)[0];
          const loginPath = (verticalSegment === 'greenhouse' || verticalSegment === 'orchard')
            ? `/${verticalSegment}/login`
            : '/login';
          window.location.href = loginPath;
        }
      }
    }
    return Promise.reject(error);
  }
);

/**
 * User-facing message from a failed API call (axios / FastAPI).
 * Prefer structured detail (e.g. vineyard_ai_token_limit_exceeded.message) over generic axios text.
 */
export function getApiErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const ax = error as { response?: { data?: { detail?: unknown } } };
    const detail = ax.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (detail && typeof detail === 'object' && detail !== null && 'message' in detail) {
      const msg = (detail as { message?: unknown }).message;
      if (typeof msg === 'string' && msg.trim()) {
        return msg;
      }
    }
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      if (typeof first === 'string') {
        return first;
      }
      if (first && typeof first === 'object' && first !== null && 'msg' in first) {
        return String((first as { msg: unknown }).msg);
      }
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Something went wrong. Please try again.';
}

export default api;

// API endpoints
export const endpoints = {
  // Auth
  login: '/api/auth/login',
  register: '/api/auth/register',
  me: '/api/auth/me',
  
  // Blocks
  blocks: '/api/blocks',
  block: (id: string) => `/api/blocks/${id}`,
  
  // Inputs
  inputs: '/api/inputs',
  input: (id: string) => `/api/inputs/${id}`,
  
  // Harvests
  harvests: '/api/harvests',
  harvest: (id: string) => `/api/harvests/${id}`,
  
  // Analytics
  profitability: '/api/analytics/profitability',
  dashboardKpis: '/api/analytics/dashboard-kpis',
  blockHistory: (id: string) => `/api/analytics/blocks/${id}/history`,
  scoutingAnalytics: '/api/analytics/scouting',
  wineTastingAnalytics: '/api/analytics/wine-tasting',
  comprehensiveAnalytics: '/api/analytics/comprehensive',
  
  // Scouting
  scouting: '/api/scouting',
  scoutingRecord: (id: string) => `/api/scouting/${id}`,
  scoutingSummary: (blockId: string) => `/api/scouting/block/${blockId}/summary`,
  
  // Weather
  weather: {
    base: '/api/weather',
    current: '/api/weather/current',
    history: '/api/weather/history',
    summary: '/api/weather/summary',
    forecast: '/api/weather/forecast',
    alerts: '/api/weather/alerts',
    stations: '/api/weather/stations',
    historicalComparison: '/api/weather/historical-comparison'
  },
  
  // Notifications
  notifications: '/api/notifications',
  notification: (id: string) => `/api/notifications/${id}`,
  notificationsUnreadCount: '/api/notifications/unread-count',
  notificationsMarkRead: (id: string) => `/api/notifications/${id}/read`,
  notificationsMarkAllRead: '/api/notifications/mark-all-read',
  notificationsPreferences: '/api/notifications/preferences',
  
  // Alerts
  alerts: '/api/alerts',
  alert: (id: string) => `/api/alerts/${id}`,
  alertResolve: (id: string) => `/api/alerts/${id}/resolve`,
  
  // Permits
  permits: '/api/permits',
  permit: (id: string) => `/api/permits/${id}`,
  permitDocument: (id: string) => `/api/permits/${id}/document`,
  permitRenew: (id: string) => `/api/permits/${id}/renew`,
  permitsExpiring: '/api/permits/expiring/list',
  permitsDashboard: '/api/permits/dashboard/stats',

  // Compliance rules (labor/overtime)
  complianceRules: '/api/compliance/rules',
}; 
